/**
 * Grade scoping for module lists — "which topics belong to this student".
 *
 * Restates `isModuleTaughtAt` from
 * Backend/functions/src/shared/curriculum/gradeResolution.ts over the
 * reference-data `ModuleItem` shape, plus the CUMULATIVE rule the curriculum
 * model defines: a student's coverage is the union of their board+subject's
 * grade-curricula *up to* their grade (see the `CurriculumSchema` comment in
 * Backend/functions/src/shared/types.ts). A Class-10 student practises Class-8
 * topics as revision; Class-11 topics are simply not theirs yet.
 *
 * Grades come from `gradeBoards` alone. The board-agnostic `grades` field is a
 * staler record of the same fact from an earlier era of the curriculum work, and
 * it cannot say WHICH board; every module now carries `gradeBoards` rows, so
 * unioning `grades` in would only re-admit the worse answer.
 *
 * Deliberately BOARD-BLIND, for the same reason `isModuleTaughtAt` is: this
 * over-offers (a CBSE-9 topic stays visible to an ICSE-9 student), and that is
 * the cheaper error for a picker. An extra plausible topic costs a student
 * nothing; the topic they wanted being absent is what makes the picker feel
 * broken.
 */
import type { ModuleItem } from './types';

type GradeScoped = Pick<ModuleItem, 'gradeBoards'> & { subject?: string };

/** Every grade this module is claimed at, by any board. */
export function moduleGrades(m: GradeScoped): number[] {
  const raw = (m.gradeBoards ?? []).map(([g]) => g);
  return [...new Set(raw.map(Number).filter((n) => Number.isFinite(n)))];
}

/**
 * Is this module introduced at or before `grade`? A module claiming no grade at
 * all stays visible — the same "no data ⇒ no gate" convention the entitlement
 * layer uses, so an unmigrated module never silently vanishes from a picker.
 */
export function isModuleAtOrBelowGrade(m: GradeScoped, grade: number): boolean {
  const grades = moduleGrades(m);
  return grades.length === 0 || grades.some((g) => g <= grade);
}

/**
 * Narrow a module list to a student's grade. An absent or unusable grade
 * (guest, incomplete profile) applies NO filter rather than an empty one.
 */
export function scopeModulesToGrade<T extends GradeScoped>(modules: T[], grade?: number): T[] {
  if (!grade || !Number.isFinite(grade)) return modules;
  return modules.filter((m) => isModuleAtOrBelowGrade(m, grade));
}
